import type { Dayjs } from "dayjs";

import InProgressIndicator from "../InProgressIndicator";
import { COPY } from "./copy";
import InternalDetails, { InternalBadge } from "./InternalDetails";
import { COLORS, FONT_FAMILY } from "./styles";
import useCountdown, {
  formatCountdownMajor,
  formatCountdownSeconds,
} from "./useCountdown";

function formatEtaTime(end: Dayjs): string {
  const tz = Intl.DateTimeFormat("en-US", {
    timeZoneName: "short",
  })
    .formatToParts(new Date())
    .find((p) => p.type === "timeZoneName")?.value;
  const base = end.format("h:mm A");
  return tz ? `${base} ${tz}` : base;
}

type Props = {
  maintenanceType: MaintenanceType;
  start: Dayjs | undefined;
  end: Dayjs | undefined;
  isInternal: boolean;
};

export default function MaintenanceCard({
  maintenanceType,
  start,
  end,
  isInternal,
}: Props) {
  const copy = COPY[maintenanceType];
  const countdown = useCountdown(end);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 28,
        width: 560,
        maxWidth: "calc(100% - 32px)",
        background: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 16,
        padding: "48px 48px 40px 48px",
        boxShadow: "0 8px 32px rgba(10, 11, 15, 0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 12px",
            background: copy.pillBg,
            border: `1px solid ${copy.pillBorder}`,
            borderRadius: 999,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: copy.pillDot,
              animation: "maintenance-pulse 2s ease-in-out infinite",
            }}
          />
          <span
            style={{
              fontFamily: FONT_FAMILY,
              fontSize: 12,
              fontWeight: 600,
              color: copy.pillText,
              letterSpacing: 0.1,
            }}
          >
            {copy.pill}
          </span>
        </div>
        {isInternal && <InternalBadge />}
      </div>
      <style>{`
        @keyframes maintenance-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      <h1
        style={{
          margin: 0,
          fontFamily: FONT_FAMILY,
          fontSize: 32,
          fontWeight: 600,
          color: COLORS.textPrimary,
          letterSpacing: -0.6,
          textAlign: "center",
        }}
      >
        {copy.title}
      </h1>

      <p
        style={{
          margin: 0,
          fontFamily: FONT_FAMILY,
          fontSize: 15,
          fontWeight: 400,
          color: COLORS.textSecondary,
          lineHeight: 1.5,
          textAlign: "center",
        }}
      >
        {copy.subtitle}
      </p>

      {countdown && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            width: "100%",
            padding: "24px 0 8px 0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatCountdownMajor(countdown) && (
              <span
                style={{
                  fontFamily: FONT_FAMILY,
                  fontSize: 56,
                  fontWeight: 600,
                  color: COLORS.textPrimary,
                  letterSpacing: -1.5,
                  lineHeight: 1,
                }}
              >
                {formatCountdownMajor(countdown)}
              </span>
            )}
            <span
              style={{
                fontFamily: FONT_FAMILY,
                fontSize: formatCountdownMajor(countdown) ? 24 : 56,
                fontWeight: formatCountdownMajor(countdown) ? 500 : 600,
                color: formatCountdownMajor(countdown)
                  ? COLORS.textTertiary
                  : COLORS.textPrimary,
                letterSpacing: formatCountdownMajor(countdown) ? -0.4 : -1.5,
                lineHeight: 1,
              }}
            >
              {formatCountdownSeconds(countdown)}
            </span>
          </div>
          <span
            style={{
              fontFamily: FONT_FAMILY,
              fontSize: 12,
              fontWeight: 500,
              color: COLORS.textTertiary,
              letterSpacing: 0.6,
            }}
          >
            estimated time remaining
          </span>
        </div>
      )}

      {end && (
        <span
          style={{
            fontFamily: FONT_FAMILY,
            fontSize: 14,
            fontWeight: 500,
            color: COLORS.textSecondary,
          }}
        >
          {copy.etaLabel(formatEtaTime(end))}
        </span>
      )}

      <InProgressIndicator
        labels={copy.progressMessages}
        labelStepMs={5000}
        compact
      />

      {isInternal && <InternalDetails start={start} end={end} />}

      <div style={{ width: "100%", height: 1, background: COLORS.border }} />

      <p
        style={{
          margin: 0,
          fontFamily: FONT_FAMILY,
          fontSize: 13,
          fontWeight: 400,
          color: COLORS.textTertiary,
          lineHeight: 1.5,
          textAlign: "center",
        }}
      >
        If this takes longer than expected, please reach out to Wieldy support.
      </p>
    </div>
  );
}
